import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";

export interface Comment {
  id: number;
  project_id?: string;
  initiative_id?: string;
  author: string;
  body: string;
  comment_type: string;
  created_at: string;
  updated_at: string;
}

export function useComments(entityId: string | null, limit = 50, apiPrefix = "/comments") {
  return useQuery<Comment[]>({
    queryKey: [apiPrefix, entityId, limit],
    queryFn: () =>
      api.get(`${apiPrefix}/${entityId}`, { params: { limit } }).then((r) => r.data),
    enabled: !!entityId,
  });
}

export function useAddComment(entityId: string, apiPrefix = "/comments") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { author: string; body: string; comment_type?: string }) =>
      api.post(`${apiPrefix}/${entityId}`, payload).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [apiPrefix, entityId] });
    },
  });
}

export function useUpdateComment(entityId: string, apiPrefix = "/comments") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: string }) =>
      api.patch(`${apiPrefix}/id/${id}`, { body }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [apiPrefix, entityId] });
    },
  });
}

export function useDeleteComment(entityId: string, apiPrefix = "/comments") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: number) =>
      api.delete(`${apiPrefix}/id/${commentId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [apiPrefix, entityId] });
    },
  });
}

/** Mirrored Freshservice conversations for a project's originating
 *  ticket (synced on the worker tick; read-only). */
export interface FsComment {
  fs_ticket_id: number;
  fs_conversation_id: number;
  author: string | null;
  body_html: string | null;
  is_private: boolean;
  fs_created_at: string | null;
  synced_at: string;
}

export function useFsComments(projectId: string, enabled: boolean) {
  return useQuery<{ comments: FsComment[] }>({
    queryKey: ["portfolio", "fs-comments", projectId],
    queryFn: () => api.get(`/portfolio/${projectId}/fs-comments`).then((r) => r.data),
    enabled: enabled && !!projectId,
  });
}
